import React, { useMemo } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../../../../views/components/common/pages/AppTableRowCell";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import {
  LeadOwnerLeaderboardMetric,
  LeadOwnerLeaderboardRow,
} from "../../../../../models/Dashboard/LeadAnalytics/LeadAnalytics";

interface LeadOwnerLeaderboardTableProps {
  rows: LeadOwnerLeaderboardRow[];
  loading?: boolean;
  selectedOwnerId?: string | null;
  selectedMetric?: LeadOwnerLeaderboardMetric | null;
  onMetricClick?: (
    row: LeadOwnerLeaderboardRow,
    metric: LeadOwnerLeaderboardMetric
  ) => void;
}

const COLUMNS = [
  { key: "rank", label: "Rank", minWidth: "78px" },
  { key: "owner_name", label: "Owner", minWidth: "200px" },
  { key: "assigned_count", label: "Leads Assigned", minWidth: "130px" },
  { key: "won_count", label: "Won", minWidth: "96px" },
  { key: "qualified_count", label: "Qualified", minWidth: "110px" },
  { key: "converted_count", label: "Won + Qualified", minWidth: "130px" },
  { key: "conversion_percent", label: "Conversion %", minWidth: "124px" },
] as const;

const formatPercent = (value: number) => {
  if (Number.isInteger(value)) return `${value}%`;
  return `${value.toFixed(1)}%`;
};

const LeadOwnerLeaderboardTable: React.FC<LeadOwnerLeaderboardTableProps> = ({
  rows,
  loading = false,
  selectedOwnerId = null,
  selectedMetric = null,
  onMetricClick,
}) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;

  const summary = useMemo(() => {
    const withConversion = rows.filter((row) => row.converted_count > 0);
    const totalAssigned = rows.reduce((sum, row) => sum + row.assigned_count, 0);
    const totalConverted = rows.reduce((sum, row) => sum + row.converted_count, 0);
    return {
      owners: rows.length,
      converters: withConversion.length,
      totalAssigned,
      totalConverted,
    };
  }, [rows]);

  const getRankStyles = (index: number) => {
    if (index === 0) {
      return {
        bg: colors.successGreenLightBackground,
        color: colors.successGreen,
        border: colors.successGreenLightBorder,
      };
    }
    if (index === 1) {
      return {
        bg: colors.lightBlue,
        color: colors.darkBlue,
        border: colors.softCardBorder,
      };
    }
    if (index === 2) {
      return {
        bg: colors.lightOrange,
        color: colors.navyText,
        border: colors.softCardBorder,
      };
    }
    return {
      bg: colors.lightGreyColor,
      color: colors.mutedSlateText,
      border: colors.softBorder,
    };
  };

  const getConversionStyles = (percent: number) => {
    if (percent >= 25) {
      return {
        bg: colors.successGreenLightBackground,
        color: colors.successGreen,
        border: colors.successGreenLightBorder,
      };
    }
    if (percent >= 10) {
      return {
        bg: colors.lightBlue,
        color: colors.darkBlue,
        border: colors.softCardBorder,
      };
    }
    if (percent > 0) {
      return {
        bg: colors.lightOrange,
        color: colors.navyText,
        border: colors.softCardBorder,
      };
    }
    return {
      bg: colors.lightGreyColor,
      color: colors.mutedSlateText,
      border: colors.softBorder,
    };
  };

  if (!loading && rows.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 140,
          border: "1px dashed",
          borderColor: "divider",
          borderRadius: 2,
          mt: 1,
          bgcolor: colors.softCardBackground,
        }}
      >
        <Typography variant="body2" sx={{ color: colors.mutedSlateText }}>
          No leads found for the selected filters.
        </Typography>
      </Box>
    );
  }

  const renderMetricChip = (
    row: LeadOwnerLeaderboardRow,
    metric: LeadOwnerLeaderboardMetric,
    value: number
  ) => {
    const isSelected =
      selectedOwnerId === row.owner_id && selectedMetric === metric;
    const canClick = value > 0 && !!onMetricClick;
    const isZero = value <= 0;

    return (
      <AppTableRowCell sx={{ py: 1.1, px: 1.25 }} textAlign="center">
        <Box
          component="button"
          type="button"
          disabled={!canClick}
          onClick={canClick ? () => onMetricClick?.(row, metric) : undefined}
          sx={{
            all: "unset",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 40,
            px: 1.1,
            py: 0.45,
            borderRadius: "8px",
            fontSize: "0.8rem",
            fontWeight: canClick || isSelected ? 700 : 500,
            lineHeight: 1.2,
            color: isZero
              ? colors.mediumGray
              : isSelected
                ? colors.purpleMain
                : colors.navyText,
            backgroundColor: isSelected
              ? colors.lightPurple
              : canClick
                ? colors.softCardBackground
                : "transparent",
            border: `1px solid ${isSelected
              ? `${colors.purpleMain}55`
              : canClick
                ? colors.softCardBorder
                : "transparent"
              }`,
            cursor: canClick ? "pointer" : "default",
            transition: "background-color 0.15s ease, border-color 0.15s ease",
            "&:hover": canClick
              ? {
                backgroundColor: colors.lightBlue,
                borderColor: colors.purpleMain,
              }
              : undefined,
            "&:disabled": {
              cursor: "default",
            },
          }}
        >
          {value}
        </Box>
      </AppTableRowCell>
    );
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        mt: 1,
        borderColor: colors.softCardBorder,
        borderRadius: 2.5,
        overflow: "hidden",
        bgcolor: colors.white,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          px: 1.75,
          py: 1.25,
          borderBottom: `1px solid ${colors.softBorder}`,
          bgcolor: colors.softCardBackground,
        }}
      >
        {[
          { label: "Owners", value: summary.owners },
          { label: "Leads assigned", value: summary.totalAssigned },
          { label: "Won + Qualified", value: summary.totalConverted },
        ].map((item) => (
          <Box
            key={item.label}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.1,
              py: 0.55,
              borderRadius: "999px",
              bgcolor: colors.white,
              border: `1px solid ${colors.softCardBorder}`,
            }}
          >
            <CustomTypography
              variant="body2"
              sx={{ fontSize: "0.72rem", color: colors.mutedSlateText }}
            >
              {item.label}
            </CustomTypography>
            <CustomTypography
              variant="heading8"
              sx={{ fontSize: "0.78rem", fontWeight: 700, color: colors.navyText }}
            >
              {item.value}
            </CustomTypography>
          </Box>
        ))}
      </Box>

      <TableContainer sx={{ maxHeight: 520 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {COLUMNS.map((column) => (
                <AppTableHeaderCell
                  key={column.key}
                  title={column.label}
                  minWidth={column.minWidth}
                  txtAlign="center"
                  fontColor={colors.navyText}
                  fontWeight={700}
                  sx={{
                    py: 1.2,
                    px: 1.25,
                    fontSize: "0.72rem",
                    letterSpacing: "0.02em",
                    textTransform: "uppercase",
                    backgroundColor: colors.lightGreyColor,
                    borderBottom: `1px solid ${colors.softBorder}`,
                  }}
                />
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              const isTopThree = index < 3;
              const isRowSelected = selectedOwnerId === row.owner_id;
              const rankStyles = getRankStyles(index);
              const conversionStyles = getConversionStyles(row.conversion_percent);

              return (
                <TableRow
                  key={`${row.owner_id}-${row.owner_name}`}
                  sx={{
                    backgroundColor: isRowSelected
                      ? colors.lightPurple
                      : isTopThree
                        ? colors.softCardBackground
                        : colors.white,
                    borderLeft: isTopThree
                      ? `3px solid ${rankStyles.color}`
                      : "3px solid transparent",
                    "&:hover": {
                      backgroundColor: colors.lightBlue,
                    },
                    "& td": {
                      borderBottom: `1px solid ${colors.softBorder}`,
                    },
                  }}
                >
                  <AppTableRowCell sx={{ py: 1.15, px: 1.25 }} textAlign="center">
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 36,
                        height: 28,
                        px: 0.9,
                        borderRadius: "8px",
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        color: rankStyles.color,
                        backgroundColor: rankStyles.bg,
                        border: `1px solid ${rankStyles.border}`,
                      }}
                    >
                      #{index + 1}
                    </Box>
                  </AppTableRowCell>

                  <AppTableRowCell
                    textAlign="center"
                    fontSize="0.82rem"
                    fontWeight={isTopThree ? 700 : 600}
                    color={colors.navyText}
                    sx={{ py: 1.15, px: 1.25 }}
                  >
                    {row.owner_name}
                  </AppTableRowCell>

                  {renderMetricChip(row, "assigned", row.assigned_count)}
                  {renderMetricChip(row, "won", row.won_count)}
                  {renderMetricChip(row, "qualified", row.qualified_count)}

                  <AppTableRowCell
                    fontSize="0.82rem"
                    fontWeight={700}
                    color={
                      row.converted_count > 0
                        ? colors.navyText
                        : colors.mediumGray
                    }
                    sx={{ py: 1.15, px: 1.25 }}
                  >
                    {row.converted_count}
                  </AppTableRowCell>

                  <AppTableRowCell sx={{ py: 1.15, px: 1.25 }} textAlign="center">
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 54,
                        px: 1,
                        py: 0.4,
                        borderRadius: "999px",
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        color: conversionStyles.color,
                        backgroundColor: conversionStyles.bg,
                        border: `1px solid ${conversionStyles.border}`,
                      }}
                    >
                      {formatPercent(row.conversion_percent)}
                    </Box>
                  </AppTableRowCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default LeadOwnerLeaderboardTable;
